import { Grid } from '@mui/material';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect } from 'react';
import useRegion from '../../../hooks/useRegion';
import Input from '../../../components/Input/Input';

interface SearchPreventorFiltersProps {
    extraFilters: any;
    setExtraFilters: Dispatch<SetStateAction<any>>;
    handleChangeExtraFilters: (e: any) => void;
    isShowMoreFiltersOpen?: boolean;
    translate: (key: string) => string;
    providerOptions: ProviderOption[];
    selectedProvider?: ProviderOption | null;
    isProviderFilterLocked?: boolean;
    isFetchingProviders: boolean;
    assignPreventorRegion: ReturnType<typeof useRegion>;
}

interface ProviderOption {
    id: number;
    razonSocial: string;
    responsable?: {
        nombre?: string;
        apellido?: string;
    };
}

const SearchPreventorFilters = ({
    extraFilters = {},
    handleChangeExtraFilters,
    providerOptions,
    selectedProvider = null,
    isProviderFilterLocked = false,
    isFetchingProviders,

    assignPreventorRegion,
    translate
}: SearchPreventorFiltersProps) => {
    const { t } = useTranslation('visitas');

    const {
        provincesList,
        citiesList,
        isLoadingProvinces,
        isLoadingCities,
        selectedProvince,
        selectedCity,
        handleProvinceChange,
        handleCityChange
    } = assignPreventorRegion;

    useEffect(() => {
        if (!selectedProvince?.id) return;

        handleChangeExtraFilters({
            target: {
                name: 'idProvincia',
                value: selectedProvince?.id
            }
        });
    }, [handleChangeExtraFilters, selectedProvince?.id]);

    useEffect(() => {
        if (!selectedCity?.id) return;

        handleChangeExtraFilters({
            target: {
                name: 'idLocalidad',
                value: selectedCity?.id
            }
        });
    }, [handleChangeExtraFilters, selectedCity?.id]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={6}>
                <AutocompleteComponent
                    label={t('filters.province')}
                    size="small"
                    name="idProvincia"
                    onChange={handleProvinceChange}
                    value={selectedProvince || null}
                    options={provincesList?.data?.provincias}
                    loading={isLoadingProvinces}
                    disabled={isLoadingProvinces}
                    clearable
                />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
                <AutocompleteComponent
                    label={t('filters.city')}
                    size="small"
                    name="idLocalidad"
                    onChange={handleCityChange}
                    value={selectedCity || null}
                    options={citiesList?.data?.localidades || []}
                    clearable
                    templateLabel={(option: any) => option?.nombre}
                    disabled={!selectedProvince || isLoadingCities}
                    loading={isLoadingCities}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
                <AutocompleteComponent
                    value={isProviderFilterLocked ? selectedProvider : extraFilters?.provider || null}
                    options={providerOptions || []}
                    size="small"
                    name="provider"
                    required={true}
                    loading={isFetchingProviders}
                    disabled={isProviderFilterLocked || isFetchingProviders}
                    label={t('drawers.assignPreventor.filters.provider')}
                    templateLabel={(option: any) => {
                        const responsibleLastName = option?.responsable?.apellido;
                        const responsibleFirstName = option?.responsable?.nombre;

                        const responsibleLabel =
                            responsibleLastName && responsibleFirstName
                                ? ` (${responsibleLastName} ${responsibleFirstName})`
                                : '';

                        return `${option?.razonSocial}${responsibleLabel}`;
                    }}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'provider',
                                value: value
                            }
                        });
                    }}
                    clearable={!isProviderFilterLocked}
                />
            </Grid>
        </Grid>
    );
};

export default SearchPreventorFilters;
